import { DateTime } from "luxon";
import { formatDateTime } from "utils/dates";

import { Sector, type SectorJson } from "./sector";

type ShowJson = {
  id: number;
  name: string;
  startDate?: string;
  endDate?: string;
  doorTime?: string;
  salesStart?: null;
  seatChart?: number;
  chartUrl?: string;
  sectors: SectorJson[];
};

class Show {
  private readonly id: number | "combo";
  private readonly name: string;
  private readonly seatChart?: number;
  private readonly chartUrl?: string;
  private readonly sectors: readonly Sector[];
  private readonly startDate: string;

  static fromJson(json: ShowJson, noServiceCharge: boolean) {
    return new Show(
      json.id,
      json.name,
      json.sectors.map((sector) => Sector.fromJson(sector, noServiceCharge)),
      json.startDate,
      json.seatChart,
      json.chartUrl,
    );
  }

  constructor(
    id: Show["id"],
    name: string,
    sectors: Sector[] = [],
    startDate = "",
    seatChart?: number,
    chartUrl?: string,
  ) {
    this.id = id;
    this.name = name;
    this.seatChart = seatChart;
    this.chartUrl = chartUrl;
    this.startDate = startDate;
    this.sectors = sectors.sort((a, b) => (a.getName() > b.getName() ? 1 : -1));
  }

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getChartUrl() {
    return this.chartUrl;
  }

  getSeatChart() {
    return this.seatChart;
  }

  getDescription() {
    if (!this.startDate) {
      return this.name;
    }
    const startDate = formatDateTime(this.startDate);

    return this.name ? `${this.name} (${startDate})` : startDate;
  }

  isId(id: string | number) {
    return this.id.toString() === id.toString();
  }

  isBefore(otherShow: Show) {
    return DateTime.fromISO(this.startDate) < DateTime.fromISO(otherShow.startDate);
  }

  findSectorByUuid(uuid: string) {
    return this.sectors.find((sector) => sector.getUuid() === uuid);
  }

  mapSectors<T>(mapper: (sector: Sector) => T) {
    return this.sectors.map(mapper);
  }

  getAvailableSectionsUuids() {
    return this.sectors.flatMap((sector) => sector.getAvailableSectionsUuids());
  }

  getUniqueSector() {
    return this.sectors.length === 1 ? this.sectors[0] : undefined;
  }
}

export type { ShowJson };
export { Show };
